import React, { useState, useEffect, useRef } from 'react';
import './videoControls.css';
import { useTranslation } from 'react-i18next';

const VideoControls = ({ videoRef, isVisible = true, customDuration = 0 }) => {
    const { t } = useTranslation();
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [isSeeking, setIsSeeking] = useState(false);
    const seekbarRef = useRef(null);

    // Effective duration: use customDuration if set and less than actual, else actual
    const effectiveDuration = customDuration > 0
        ? (duration > 0 ? Math.min(customDuration, duration) : customDuration)
        : duration;

    // Format time as MM:SS
    const formatTime = (seconds) => {
        if (!seconds || isNaN(seconds)) return '00:00';
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    // Update time and duration from video
    useEffect(() => {
        const video = videoRef?.current;
        if (!video) return;

        const handleTimeUpdate = () => {
            if (!isSeeking) {
                setCurrentTime(video.currentTime);
            }
        };

        const handleLoadedMetadata = () => {
            setDuration(video.duration);
        };

        const handlePlay = () => {
            setIsPlaying(true);
        };

        const handlePause = () => {
            setIsPlaying(false);
        };

        video.addEventListener('timeupdate', handleTimeUpdate);
        video.addEventListener('loadedmetadata', handleLoadedMetadata);
        video.addEventListener('play', handlePlay);
        video.addEventListener('pause', handlePause);

        // Initialize state
        if (video.duration) setDuration(video.duration);
        setIsPlaying(!video.paused);

        return () => {
            video.removeEventListener('timeupdate', handleTimeUpdate);
            video.removeEventListener('loadedmetadata', handleLoadedMetadata);
            video.removeEventListener('play', handlePlay);
            video.removeEventListener('pause', handlePause);
        };
    }, [videoRef, isSeeking]);

    // Handle play/pause toggle
    const handlePlayPause = (e) => {
        e.stopPropagation();
        const video = videoRef?.current;
        if (!video) return;

        if (video.paused) {
            video.play().catch(err => console.warn('Play failed:', err));
        } else {
            video.pause();
        }
    };

    // Handle seekbar click
    const handleSeekbarClick = (e) => {
        e.stopPropagation();
        const video = videoRef?.current;
        const seekbar = seekbarRef.current;
        if (!video || !seekbar) return;

        const rect = seekbar.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const percentage = clickX / rect.width;
        const newTime = Math.min(percentage * effectiveDuration, effectiveDuration);

        video.currentTime = newTime;
        setCurrentTime(newTime);
    };

    // Handle seekbar drag start
    const handleSeekbarMouseDown = (e) => {
        e.stopPropagation(); // Prevent area drag
        e.preventDefault(); // Prevent text selection
        setIsSeeking(true);

        const handleMouseMove = (moveEvent) => {
            moveEvent.stopPropagation();
            const video = videoRef?.current;
            const seekbar = seekbarRef.current;
            if (!video || !seekbar) return;

            const rect = seekbar.getBoundingClientRect();
            const clickX = Math.max(0, Math.min(moveEvent.clientX - rect.left, rect.width));
            const percentage = clickX / rect.width;
            const newTime = Math.min(percentage * effectiveDuration, effectiveDuration);

            video.currentTime = newTime;
            setCurrentTime(newTime);
        };

        const handleMouseUp = (upEvent) => {
            upEvent.stopPropagation(); // Prevent area drag on release
            setIsSeeking(false);
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    // Progress capped at effectiveDuration
    const clampedTime = Math.min(currentTime, effectiveDuration);
    const progress = effectiveDuration > 0 ? (clampedTime / effectiveDuration) * 100 : 0;

    return (
        <div className={`video-controls ${isVisible ? 'visible' : ''}`}>
            {/* Play/Pause Button */}
            <button 
                className="control-button" 
                onClick={handlePlayPause}
                aria-label={isPlaying ? t("devices.preview_pause") : t("devices.preview_play")}
            >
                <i className={`fas ${isPlaying ? 'fa-pause' : 'fa-play'}`}></i>
            </button>

            {/* Seekbar */}
            <div 
                ref={seekbarRef}
                className="seekbar-container" 
                onClick={handleSeekbarClick}
                onMouseDown={handleSeekbarMouseDown}
            >
                <div className="seekbar-progress" style={{ width: `${progress}%` }}>
                    <div className="seekbar-thumb"></div>
                </div>
            </div>

            {/* Time Display */}
            <div className="time-display">
                {formatTime(clampedTime)} / {formatTime(effectiveDuration)}
            </div>
        </div>
    );
};

export default VideoControls;
